import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Smartphone, Mail, Loader2, CheckCircle, XCircle, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function SecuritySettings() {
  const { user } = useAuth();
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaMethod, setTwoFaMethod] = useState('');
  const [setupStep, setSetupStep] = useState(null); // null, 'choose', 'verify'
  const [selectedMethod, setSelectedMethod] = useState('email');
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await api.get('/auth/me');
      setTwoFaEnabled(res.data.two_factor_enabled || false);
      setTwoFaMethod(res.data.two_factor_method || '');
    } catch {}
  };

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await api.post('/auth/2fa/setup', { method: selectedMethod });
      setSetupData(res.data);
      setSetupStep('verify');
      if (selectedMethod === 'email') {
        await api.post('/auth/2fa/send-code', { email: user?.email });
        toast.success('Codice inviato alla tua email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore setup 2FA');
    } finally { setLoading(false); }
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      await api.post('/auth/2fa/verify-setup', { code: verifyCode });
      toast.success('2FA attivato con successo!');
      setTwoFaEnabled(true);
      setTwoFaMethod(selectedMethod);
      setSetupStep(null);
      setVerifyCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Codice non valido');
    } finally { setLoading(false); }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword });
      toast.success('2FA disattivato');
      setTwoFaEnabled(false);
      setTwoFaMethod('');
      setDisablePassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <PageHeader title="Sicurezza" subtitle="Autenticazione a due fattori (2FA)" />
      <div className="content-area max-w-2xl" data-testid="security-settings-page">
        {/* Current Status */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-sky-500" />
            <h2 className="text-lg font-semibold">Stato 2FA</h2>
          </div>
          <div className="flex items-center gap-3">
            {twoFaEnabled ? (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Attivo</span>
                <span className="text-sm text-slate-500">({twoFaMethod === 'totp' ? 'App Autenticatore' : 'Email OTP'})</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-slate-400">
                <XCircle className="w-5 h-5" />
                <span>Non attivo</span>
              </div>
            )}
          </div>
        </div>

        {/* Setup or Disable */}
        {!twoFaEnabled && !setupStep && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold mb-4">Attiva 2FA</h3>
            <p className="text-sm text-slate-500 mb-6">
              Proteggi il tuo account con un secondo fattore di autenticazione. Ad ogni login ti verra' chiesto un codice oltre alla password.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <button onClick={() => setSelectedMethod('email')}
                className={`p-4 rounded-xl border-2 text-left transition ${selectedMethod === 'email' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-sky-200'}`}
                data-testid="2fa-method-email">
                <Mail className="w-6 h-6 text-sky-500 mb-2" />
                <div className="font-medium text-sm">Email OTP</div>
                <p className="text-xs text-slate-500 mt-1">Ricevi un codice a 6 cifre via email ad ogni login</p>
              </button>
              <button onClick={() => setSelectedMethod('totp')}
                className={`p-4 rounded-xl border-2 text-left transition ${selectedMethod === 'totp' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:border-sky-200'}`}
                data-testid="2fa-method-totp">
                <Smartphone className="w-6 h-6 text-sky-500 mb-2" />
                <div className="font-medium text-sm">App Autenticatore</div>
                <p className="text-xs text-slate-500 mt-1">Google Authenticator, Authy o simili</p>
              </button>
            </div>
            <Button onClick={handleSetup} disabled={loading} className="bg-sky-500 hover:bg-sky-600" data-testid="2fa-setup-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Configura 2FA
            </Button>
          </div>
        )}

        {/* Verify Step */}
        {setupStep === 'verify' && (
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold mb-4">Verifica configurazione</h3>
            {selectedMethod === 'totp' && setupData && (
              <div className="mb-6">
                <p className="text-sm text-slate-600 mb-4">
                  Scansiona questo QR code con Google Authenticator o Authy:
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center mb-4">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.provisioning_uri)}`}
                    alt="QR Code TOTP"
                    className="mx-auto"
                    data-testid="totp-qr-code"
                  />
                </div>
                <details className="text-xs text-slate-500">
                  <summary className="cursor-pointer hover:text-sky-500">Inserimento manuale</summary>
                  <code className="block mt-2 p-2 bg-slate-100 rounded break-all">{setupData.secret}</code>
                </details>
              </div>
            )}
            {selectedMethod === 'email' && (
              <p className="text-sm text-slate-600 mb-4">
                Abbiamo inviato un codice a 6 cifre a <strong>{user?.email}</strong>. Inseriscilo qui sotto:
              </p>
            )}
            <div className="flex gap-3">
              <Input
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value)}
                placeholder="Codice a 6 cifre"
                maxLength={6}
                className="font-mono text-center text-lg tracking-widest w-48"
                data-testid="2fa-verify-input"
              />
              <Button onClick={handleVerify} disabled={loading || verifyCode.length < 6} className="bg-sky-500 hover:bg-sky-600" data-testid="2fa-verify-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verifica e Attiva'}
              </Button>
            </div>
            <button onClick={() => { setSetupStep(null); setSetupData(null); setVerifyCode(''); }} className="text-sm text-slate-400 hover:text-slate-600 mt-4">
              Annulla
            </button>
          </div>
        )}

        {/* Disable 2FA */}
        {twoFaEnabled && (
          <div className="bg-white border border-red-200 rounded-xl p-6">
            <h3 className="font-semibold text-red-600 mb-4">Disattiva 2FA</h3>
            <p className="text-sm text-slate-500 mb-4">Inserisci la tua password per disattivare l'autenticazione a due fattori.</p>
            <div className="flex gap-3">
              <Input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)}
                placeholder="La tua password" className="max-w-xs" data-testid="2fa-disable-password" />
              <Button onClick={handleDisable} disabled={loading || !disablePassword} variant="destructive" data-testid="2fa-disable-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disattiva 2FA'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
